/*
 * WHERE DID THAT NUMBER COME FROM?
 *
 * An agent's return travels up. Its summary becomes its parent's evidence, and eventually a sentence
 * a person reads and acts on — with the agent tree standing behind it as proof the work was done. So
 * a value that entered the tree from nowhere is the most expensive kind of wrong: it is
 * indistinguishable from a measurement, and on this platform the sentence it ends up in can be about
 * a pressure vessel.
 *
 * The store already refuses the coarse version: a run cannot report COMPLETED while an agent it
 * delegated to has not answered. That catches "I have not been told and I am claiming anyway". It
 * cannot catch the finer one — a run that legitimately concludes and states a figure no tool ever
 * returned.
 *
 * THIS IS NOT A LIE DETECTOR, AND PRETENDING OTHERWISE WOULD BE THE BUG.
 *
 * Whether a number was measured, computed or invented is not decidable from the text. What IS
 * decidable, exactly and cheaply, is whether it appears anywhere in what the agent actually
 * received. That is a fact about the record, and it is the fact worth surfacing:
 *
 *     "this figure appears nowhere in this agent's task, its tool results, or the answers other
 *      agents sent it"
 *
 * — which is TRUE of a fabrication and equally true of an honest calculation. So the output is
 * evidence, never a verdict. It gets the model one chance to source or drop the value, and if the
 * value stands it travels WITH the return, visible to every reader above it. A derived total is
 * still reported; it just arrives labelled as derived rather than as measured.
 *
 * WHY NUMBERS AND IDENTIFIERS, AND NOT PROSE. Prose is where an agent is supposed to reason, and
 * checking it would flag every legitimate sentence. The things that get ACTED on are the figures and
 * the names: a pressure, a tag, a controller id. Those are also the things a reader assumes were
 * read off something.
 */

/** How many unsourced values are worth naming. Past a handful the list stops being a finding and
 *  starts being a wall of text nobody reads — and a deliverable with twenty unsourced numbers has
 *  already made the point. */
const MAX_REPORTED = 6;

/**
 * Numbers, normalised so that formatting is not mistaken for invention.
 *
 * `4.21`, `4.210`, `"4.21 bar"` and `4.21` inside a sentence are the same measurement, and a check
 * that called three of them unsourced would be noise within a day. Trailing zeros go, a leading `+`
 * goes, and `-0` is `0`.
 */
function numbersIn(text: string): Set<string> {
  return new Set(numbersAt(text).map((n) => n.value));
}

/** The same numbers, with where each one starts — for pairing a value with the name nearest to it. */
function numbersAt(text: string): { value: string; index: number; end: number }[] {
  const out: { value: string; index: number; end: number }[] = [];
  /*
   * A DIGIT INSIDE A NAME IS NOT A VALUE. "Station B3", "OB1", "run-75cc…": read digit-by-digit,
   * a list of eighteen models yields a 3 that nothing measured, and the check then reports that
   * the person's "3 seconds" — which came from nobody's reading — came back from the gateway that
   * listed Station B3. So a number must start a token: not glued to a letter, a digit or an
   * underscore before it. A trailing unit ("3s", "4.21bar") is still a value.
   *
   * NOR IS A DIGIT RUN INSIDE A HYPHENATED NAME. "mock-938637234be8", "PT-1042", "run-75cc…": the
   * hyphen let the digits start a token, so a controller id echoed by every dashboard call became a
   * nine-digit "reading" that belonged to whichever subject was asked first — and an agent that
   * merely named the controller on the next subject was accused of swapping a measurement. A
   * hyphen glued to a name character before it is part of the name; "-12.5" after a space is
   * still a negative number.
   */
  for (const m of withoutClocks(text).matchAll(/(?<![\p{L}\d_])(?<![\p{L}\d_]-)-?\d[\d,]*\.?\d*(?:[eE][+-]?\d+)?/gu)) {
    const n = Number(m[0].replace(/,/g, ''));
    if (Number.isFinite(n)) out.push({ value: canonical(n), index: m.index, end: m.index + m[0].length });
  }
  return out;
}

/**
 * A TIME IS NOT THREE NUMBERS, and a date is not two.
 *
 * "14:31:58" read digit-group by digit-group yields 14, 31 and 58 — three findings, none of which
 * anybody reported. An agent naming when something happened is the ordinary case, so left in, this
 * fires on most real answers and the check is ignored within a day.
 *
 * Removed from BOTH sides, so a timestamp is neither claimed nor able to source a genuine reading
 * that happens to share its digits.
 */
const withoutClocks = (text: string): string =>
  text
    // Blanked to the same length, so a position found in the result is a position in the text.
    .replace(/\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?Z?)?/g, (m) => ' '.repeat(m.length))
    // Not `\b` at the end: in "17:37:43Z" there is no word boundary between 3 and Z, so a UTC time
    // was left in as 17, 37 and 43, and an answer that gave its window in UTC was asked where those came from.
    .replace(/\b\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?!\d)/g, (m) => ' '.repeat(m.length));

/**
 * A COUNT, AN INDEX OR A YEAR — present in nearly every answer, so it proves nothing.
 *
 * Block 1, network 1, "13 times", 2026: matching one of these to a call is a coincidence, not
 * provenance. All three checks use this one rule; the wrong-subject check went without it and
 * accused correct answers of swapping a block number with the relay that listed the blocks.
 */
function isIncidental(v: string): boolean {
  const n = Number(v);
  return Number.isInteger(n) && (Math.abs(n) <= 10 || (n >= 1900 && n <= 2100));
}

/** One spelling per value, so a match is a match however either side wrote it. */
function canonical(n: number): string {
  if (Object.is(n, -0)) return '0';
  // Enough precision for any sensor reading; short enough that 0.1+0.2 does not become its own value.
  return String(Number(n.toPrecision(12)));
}

/**
 * The values a deliverable ASSERTS — its leaves, not its shape.
 *
 * Keys are excluded deliberately: `{"pressure_bar": 4.21}` asserts 4.21, and the `_bar` in the key
 * is a label the agent chose rather than a figure it is reporting. Booleans and nulls are excluded
 * for the same reason — nobody reads `true` off an instrument.
 */
/*
 * SOURCE IS NOT A CLAIM. A widget author returns its widget as JavaScript and a statechart author
 * returns SCXML — by value, in the deliverable — and every one of those carries numbers that are
 * design, not measurement: a scale divisor, a shelf margin, a lid-width fraction, a timer delay.
 * Read as figures they were "asserted" by nobody, and the guard challenged both authors on every
 * hand-over ("192, 18, 0.05, 74, 58, 0.14 appear nowhere in your task"), a round trip spent
 * explaining that 0.14 is how wide a jar lid is. A string that reads as code or markup is skipped
 * whole; the summary beside it is still read, which is where a measurement would be stated.
 */
const looksLikeSource = (s: string): boolean =>
  s.length > 120 && s.includes('\n') && /\bfunction\b|=>|\breturn\b|\b(?:const|let|var)\s+\w+\s*=|<\/?[a-z][\w:-]*[\s>\/]/.test(s);

/** Whether any string in the value, at any depth, reads as code or markup. */
function carriesSource(value: unknown): boolean {
  if (typeof value === 'string') return looksLikeSource(value);
  if (Array.isArray(value)) return value.some(carriesSource);
  if (value && typeof value === 'object') return Object.values(value as Record<string, unknown>).some(carriesSource);
  return false;
}

function assertedNumbers(value: unknown, into = new Set<string>()): Set<string> {
  if (typeof value === 'number' && Number.isFinite(value)) into.add(canonical(value));
  else if (typeof value === 'string') {
    if (!looksLikeSource(value)) for (const n of numbersIn(value)) into.add(n);
  } else if (Array.isArray(value)) for (const v of value) assertedNumbers(v, into);
  else if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      // A SELF-ASSESSMENT IS NOT A MEASUREMENT. Asking an agent where its confidence came from is a
      // category error — it came from the agent, which is what the field means. Reporting one is
      // also completely ordinary, so flagging it would put a finding on most honest answers.
      if (SELF_ASSESSED.test(k)) continue;
      assertedNumbers(v, into);
    }
  }
  return into;
}

/** Fields whose value the agent is SUPPOSED to invent, because they describe its own judgement. */
const SELF_ASSESSED = /confiden|certain|likelihood|probabilit|\bscore\b|_score$|^score/i;

/**
 * Values that appear in the deliverable and nowhere in the evidence.
 *
 * `evidence` is everything the run actually received, concatenated: its objective and inputs, every
 * tool result, and every message handed to it — a peer's note, a child's return, the user's own
 * words. Concatenated rather than structured because the question is only "does this appear", and
 * the answer must not depend on which slot it appeared in.
 *
 * SMALL INTEGERS ARE IGNORED. 0 through 10 turn up in counts, indices, list positions and ordinary
 * English, so treating them as findings would flag almost every deliverable while catching nothing:
 * a fabricated pressure is 4.21, not 3. Years are ignored for the same reason — they are context,
 * not readings.
 */
export function unsourcedValues(deliverableJson: string, summary: string, evidence: string): string[] {
  let parsed: unknown;
  try {
    parsed = deliverableJson.trim() ? JSON.parse(deliverableJson) : undefined;
  } catch {
    // An unparseable deliverable is the contract check's problem, not this one. Saying nothing is
    // right: this function reports what it can prove, and it can prove nothing here.
    return [];
  }

  /*
   * AN ARTIFACT IS NOT A REPORT. A deliverable that carries source — a widget module, a statechart —
   * is a thing that was MADE, and every number beside the source is a dimension of it: a preferred
   * width of 420, a minimum of 350, an aspect, a frame rate. Skipping the source string alone still
   * challenged those ("420, 350 appear nowhere in your task"), so the whole object is read as
   * design when any part of it is source. The summary beside it is still read: a measurement stated
   * in prose is a claim whatever it accompanies.
   */
  const claimed = carriesSource(parsed) ? new Set<string>() : assertedNumbers(parsed);
  for (const n of numbersIn(summary)) claimed.add(n);

  const have = numbersIn(evidence);
  const haveValues = [...have].map(Number);
  const out: string[] = [];
  for (const v of claimed) {
    if (have.has(v)) continue;
    if (isIncidental(v)) continue; // counts, indices and years, not readings
    if (roundedFrom(v, haveValues)) continue;
    out.push(v);
    if (out.length >= MAX_REPORTED) break;
  }
  return out;
}

/*
 * A FIGURE ROUNDED FROM A READING IS THAT READING. An analysis prints 34.73 and the answer says
 * 34.7; a tool returns 1200.5 seconds and the answer says 1,200. Compared exactly, those were
 * "nowhere in what you received", and on 2026-10-28 every Data Scientist run was asked about them:
 * 11 of the 16 figures named were roundings like these. A claim is sourced when some received value,
 * rounded to the claim's own decimal places, is the claim.
 */
function roundedFrom(claim: string, received: readonly number[]): boolean {
  const c = Number(claim);
  const places = claim.includes('.') ? claim.length - claim.indexOf('.') - 1 : 0;
  const half = 0.5 * 10 ** -places + 1e-9; // the float slack keeps 1200.5 -> 1200 from failing by a hair
  return received.some((n) => Math.abs(n - c) <= half);
}

/**
 * What to tell the model, in the words that make it act.
 *
 * Not "invalid" and not an accusation: it is asked to do one of two things it can actually do —
 * point at where the figure came from, or say it does not have it. An agent that measured something
 * can cite it; an agent that computed it can say so; an agent that invented it has nothing to write,
 * which is the moment the check is for.
 */
export function unsourcedRemedy(values: readonly string[]): string {
  const list = values.join(', ');
  return (
    `these figures appear nowhere in your task, your tool results, or anything another agent sent you: ${list}. ` +
    'Either report where each came from, or say that you do not have it. Do not state a measurement you were not given.'
  );
}

/*
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE VALUE IS REAL AND IT BELONGS TO SOMETHING ELSE.
 *
 * `unsourcedValues` closes the case where a figure came from nowhere. It cannot see the one that
 * matters more often in practice: a figure that IS in the record, reported against the wrong thing.
 * The agent reads PT-1042 and gets 4.21, reads TT-9 and gets 8.7, and writes "TT-9 reads 4.21". Every
 * number is sourced. The sentence is false, and it is false in the way that gets someone hurt —
 * a real reading, attached to the wrong vessel.
 *
 * WHAT MAKES THIS DECIDABLE where "is this true" is not: we know what the agent ASKED for. A tool
 * call carries its arguments, and the arguments name the subject — a path, a tag, an id. So for a
 * claim that pairs subject X with value V we can ask a question with an exact answer:
 *
 *     we called for X. does what came back contain V?
 *
 * If it does not, and V is in the answer to a call for something ELSE, the claim contradicts the
 * record. Not "unlikely" — contradicts. That is the only case reported here, which is why it can be
 * stated flatly rather than hedged.
 *
 * DELIBERATELY NARROW, three times over:
 *   • only subjects we actually called for — never a name the agent merely mentioned
 *   • only values that appear in some OTHER call's answer — a converted or computed figure belongs
 *     to nothing and is `unsourcedValues`'s business, not a swap
 *   • only pairs that occur TOGETHER — in one object, or one sentence
 *
 * Drop the last of those and it fires on every correct multi-reading answer ("PT-1 is 4.21 and TT-9
 * is 8.7" pairs PT-1 with 8.7 as readily as with 4.21), which is the shape most real answers take.
 */

/** One tool call as the record holds it: what was asked, and what came back. */
export interface RecordedCall {
  name: string;
  argumentsJson: string;
  output: string;
}

/** A claim that contradicts the record. */
export interface Misattribution {
  value: string;
  /** The subject the claim attached it to. */
  claimedOf: string;
  /** The subject whose answer actually contains it. */
  actuallyOf: string;
}

/**
 * Strings from a call's arguments that name something — a tag, a path, an id.
 *
 * Bounded to things that LOOK like identifiers, because arguments also carry prose: a `query` or a
 * `reason` is a sentence, and treating its words as subjects would pair half the English language
 * with every number in the answer. An identifier has a digit or a separator in it and no spaces,
 * which is what a tag, a path and a uuid all have and a sentence does not.
 */
/*
 * ARGUMENTS THAT SAY WHERE TO LOOK, NOT WHAT WAS READ.
 *
 * A relay and a controller are in the arguments of nearly every call, so as subjects they owned every
 * number that came back, and every correct answer looked like a swap: on the local log to 28 October,
 * all 29 wrong-subject findings named a relay or a controller as the real owner. A search term is the
 * same kind of thing: `query: "Conveyor1"` names what to look for, and the values that come back
 * belong to what was found. Names taken from the tools' own argument names, counted on that log.
 */
const NOT_A_SUBJECT = new Set(['gateway', 'gateway_id', 'gatewayId', 'controller', 'controller_id', 'controllerId', 'query', 'find']);

function subjectsOf(argumentsJson: string): string[] {
  let args: unknown;
  try {
    args = JSON.parse(argumentsJson);
  } catch {
    return [];
  }
  if (!args || typeof args !== 'object' || Array.isArray(args)) return [];
  /*
   * TOP-LEVEL ONLY. A read names what it reads at the top: `symbolPath`, `blockId`, `modelId`,
   * `paths: [...]`. Deeper down is something else. `sandbox.run` nests the INPUTS of a calculation
   * (`datasets[].symbolPath`), and what it returns is computed, not read from them; `models.write`
   * nests a document the agent wrote. Read as subjects, a figure Python computed was "owned" by a
   * dataset, and a count from a replay was a swap against a sensor named inside the model.
   */
  const out: string[] = [];
  const take = (v: unknown): void => {
    if (typeof v !== 'string') return;
    const s = v.trim();
    if (s.length >= 3 && s.length <= 200 && !/\s/.test(s) && /[\d/\-_.:]/.test(s) && /[A-Za-z]/.test(s)) out.push(s);
  };
  for (const [key, v] of Object.entries(args)) {
    if (NOT_A_SUBJECT.has(key)) continue;
    if (Array.isArray(v)) v.forEach(take);
    else take(v);
  }
  return out;
}

/*
 * A NAME IS A WHOLE NAME. Found by substring, DB1 was "mentioned" in every sentence about DB10 and
 * PT-1 in every sentence about PT-1042. A mention may not run on into more of a name: a letter, digit
 * or underscore beside it, or a `.` or `-` that joins it to one (DB1.Run). A path at the end of a
 * sentence keeps its full stop and is still a mention.
 */
function mentions(text: string, name: string): { index: number; end: number }[] {
  const out: { index: number; end: number }[] = [];
  const word = /[\p{L}\d_]/u;
  const joins = (a: string | undefined, b: string | undefined): boolean =>
    (a !== undefined && word.test(a)) || ((a === '.' || a === '-') && b !== undefined && word.test(b));
  for (let i = text.indexOf(name); i >= 0; i = text.indexOf(name, i + 1)) {
    const end = i + name.length;
    if (joins(text[i - 1], text[i - 2]) || joins(text[end], text[end + 1])) continue;
    out.push({ index: i, end });
  }
  return out;
}

/*
 * EACH NUMBER BELONGS TO THE NAME NEAREST IT. "PT-1 is 4.21 and TT-9 is 8.7" is one sentence and two
 * correct readings; paired every-with-every it reported both as swaps. A number equally near two
 * names is not paired at all: there is no claim there that can be checked.
 */
function nearestPairs(text: string, subjects: ReadonlySet<string>): { subject: string; value: string }[] {
  const at = [...subjects].flatMap((s) => mentions(text, s).map((m) => ({ s, ...m })));
  if (!at.length) return [];
  const out: { subject: string; value: string }[] = [];
  for (const n of numbersAt(text)) {
    let best: string | undefined;
    let bestGap = Infinity;
    let tie = false;
    for (const m of at) {
      const gap = n.index >= m.end ? n.index - m.end : m.index - n.end;
      if (gap < bestGap) [best, bestGap, tie] = [m.s, gap, false];
      else if (gap === bestGap && m.s !== best) tie = true;
    }
    if (best && !tie) out.push({ subject: best, value: n.value });
  }
  return out;
}

/**
 * (subject, value) pairs the claim actually makes — where they appear TOGETHER.
 *
 * Together means one object in the deliverable, or one sentence in the prose. Anything wider and a
 * correct answer about two instruments pairs each reading with both of them, so the check would
 * report a swap on exactly the answers that got it right.
 */
function claimedPairs(parsed: unknown, summary: string, subjects: ReadonlySet<string>): { subject: string; value: string }[] {
  const pairs: { subject: string; value: string }[] = [];
  const named = (text: string): string[] => [...subjects].filter((s) => mentions(text, s).length > 0);

  /*
   * The deliverable, object by object.
   *   • A key names its value: `{"PT-1042": 4.21}` says exactly what it looks like.
   *   • A string that names something is read like a sentence: each number to its nearest name.
   *   • The other numbers in the object — its number fields, and strings that name nothing — belong
   *     to the object's subject when it has exactly one. With two or more there is no telling which.
   */
  const walk = (v: unknown): void => {
    if (Array.isArray(v)) return v.forEach(walk);
    if (!v || typeof v !== 'object') return;
    const entries = Object.entries(v as Record<string, unknown>);
    const loose = new Set<string>();
    const inObject = new Set<string>();
    for (const [k, val] of entries) {
      const keyed = named(k);
      const valueNums =
        typeof val === 'number' && Number.isFinite(val) ? [canonical(val)] : typeof val === 'string' ? [...numbersIn(val)] : [];
      if (keyed.length) {
        for (const s of keyed) for (const n of valueNums) pairs.push({ subject: s, value: n });
        continue;
      }
      if (typeof val === 'string' && named(val).length) {
        named(val).forEach((s) => inObject.add(s));
        pairs.push(...nearestPairs(val, subjects));
      } else for (const n of valueNums) loose.add(n);
    }
    if (inObject.size === 1) for (const n of loose) pairs.push({ subject: [...inObject][0], value: n });
    for (const [, val] of entries) walk(val);
  };
  walk(parsed);

  // The prose, sentence by sentence — the parent reads this, so a swap stated here is a swap stated.
  for (const sentence of summary.split(/(?<=[.!?])\s+|\n+/)) pairs.push(...nearestPairs(sentence, subjects));
  return pairs;
}

/**
 * Claims that contradict the record: a real value, reported against the wrong subject.
 *
 * Empty unless all three conditions hold at once — see the header. A pair whose subject we never
 * called for, or whose value appears in no answer at all, is not reported here; the first is not
 * ours to judge and the second is `unsourcedValues`'s.
 */
export function misattributed(deliverableJson: string, summary: string, calls: readonly RecordedCall[]): Misattribution[] {
  if (!calls.length) return [];
  let parsed: unknown;
  try {
    parsed = deliverableJson.trim() ? JSON.parse(deliverableJson) : undefined;
  } catch {
    return [];
  }

  // Subject → the values that came back when we asked about it. A subject asked about more than once
  // accumulates, because every answer about it is evidence about it.
  const answers = new Map<string, Set<string>>();
  /*
   * AND THE VALUES THAT CAME FROM NO SUBJECT AT ALL. A question put to the person, a web search, a
   * calculation: the call names nothing, so what it returned belongs to nothing in particular and
   * may be reported against anything. A cycle time the person typed is not a reading that was
   * taken from one vessel and attached to another — there is no swap to find.
   */
  const free = new Set<string>();
  for (const c of calls) {
    const vals = numbersIn(c.output);
    const subjects = subjectsOf(c.argumentsJson);
    if (!subjects.length) for (const v of vals) free.add(v);
    /*
     * AND THE VALUES THE AGENT WROTE ITSELF. A range it chose for a tile, a limit it set on a
     * model, an id it minted: these went OUT in the arguments, and when a read echoes them back
     * they look like readings taken from whatever the call was addressed to. A designer that
     * configured a 0–80 fill and then said so was told 80 "came back from" the gateway. An
     * authored value was read from nothing, so it may be reported against anything.
     */
    for (const v of numbersIn(c.argumentsJson)) free.add(v);
    for (const s of subjects) {
      const set = answers.get(s) ?? new Set<string>();
      for (const v of vals) set.add(v);
      answers.set(s, set);
    }
  }
  if (!answers.size) return [];

  const out: Misattribution[] = [];
  const seen = new Set<string>();
  for (const { subject, value } of claimedPairs(parsed, summary, new Set(answers.keys()))) {
    if (isIncidental(value)) continue; // a block number or a count matches some call by coincidence
    if (answers.get(subject)?.has(value)) continue; // the claim matches what we were told
    if (free.has(value)) continue; // the person (or a search) said it — it was read from nothing
    // Whose answer DID contain it? Only a swap if it belongs to somebody.
    const owner = [...answers].find(([s, vals]) => s !== subject && vals.has(value));
    if (!owner) continue; // came from nowhere — that is the other check's finding, not this one
    const key = `${value}|${subject}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ value, claimedOf: subject, actuallyOf: owner[0] });
    if (out.length >= MAX_REPORTED) break;
  }
  return out;
}

/** What to tell the model: which value, which name, which call. Specific, because the fix is a
 *  substitution it can make in one step — and not a verdict, because the agent may know better (the
 *  value may be right), and a model told it is wrong rewrites a correct answer to agree. */
export function misattributedRemedy(items: readonly Misattribution[]): string {
  const list = items
    .map((m) => `${m.value} is reported against ${m.claimedOf}, but ${m.value} came back from a call about ${m.actuallyOf}`)
    .join('; ');
  return `${list}. Check which reading belongs to which.`;
}

/*
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * WHERE EACH FIGURE CAME FROM — derived, never asked for, and never inside the deliverable.
 *
 * `misattributed` catches a value reported against a subject we CALLED for and disagreed with. It
 * cannot judge a subject we never called for: an agent that reads PT-1042 and reports the figure
 * against "COPV-7" may be right — COPV-7 could be the vessel that tag sits on — and deciding
 * whether two names mean the same thing is a modelling question the platform genuinely cannot
 * answer.
 *
 * What it CAN do is attach the origin to the figure, so a wrong pairing is apparent at a glance:
 *
 *     4.21 ← readSymbol(PT-1042)
 *
 * A reader — human or the parent model — sees the claim say TT-9 and the citation say PT-1042. No
 * adjudication required, and no false accusation possible, because a citation states a fact rather
 * than a verdict.
 *
 * DERIVED, NOT REQUESTED. Asking the model to cite is asking it to get a second thing right, and a
 * citation it authors is one it can get wrong in the same breath as the claim. The call records
 * already say which answer contained which value, so the platform can compute it — and a computed
 * citation cannot be falsified by the thing being cited.
 *
 * BESIDE THE DELIVERABLE, NEVER INSIDE IT. The deliverable is the expert's declared `output_schema`,
 * validated at the boundary and handed to an SCXML transition as `_event.data` — a W3C-standard
 * event payload that a `cond` branches on. Wrapping a value as `{value, from}` to carry provenance
 * would change what every guard reads and break every state machine written against it. Citations
 * ride on the return envelope; the payload is untouched.
 */

/** One figure and where it came from. */
export interface Citation {
  value: string;
  /** The calls whose answer contained it, described as `name(subject)`. More than one is honest
   *  ambiguity, not an error — two reads can legitimately return the same number. */
  sources: string[];
}

/** `readSymbol(PT-1042)`, or just `readSymbol` when the arguments name nothing identifiable. */
function describe(call: RecordedCall): string {
  const subjects = subjectsOf(call.argumentsJson);
  return subjects.length ? `${call.name}(${subjects.join(', ')})` : call.name;
}

/**
 * Where each figure in the DELIVERABLE came from.
 *
 * The deliverable rather than the prose, because the deliverable is the part a machine acts on — an
 * SCXML guard branches on it, and that is the claim whose provenance decides something. Prose is
 * covered by the two findings above, which is the right division: prose gets checked, structure gets
 * sourced.
 *
 * Bounded, because this is a line on a return that a parent reads on every step it has left, and a
 * bibliography of thirty entries is not a line.
 */
/*
 * WHAT A TOOL ACTUALLY REPORTED, as opposed to what its output happens to contain.
 *
 * The two provenance questions want OPPOSITE strictness from the same bytes, and reading them the
 * same way is what produced a citation line reading
 *
 *     Sources 249 <- send_agent_note; -42 <- send_agent_note; 656 <- send_agent_note
 *
 * on a tool that reports no measurements at all. Those were incidental integers — offsets, lengths,
 * ids — sitting in the tool's prose, matched by a flat scan of its whole output.
 *
 *   • "does this figure appear ANYWHERE in what the run received?" — `unsourcedValues`, which
 *     ACCUSES. It must be generous: a number found loosely is a number we will not call invented.
 *     A flat text scan is right there and stays.
 *   • "which call REPORTED this figure?" — a citation, which VOUCHES. It must be strict: a citation
 *     is a claim that the tool measured the thing, and a wrong one is worse than none, because it
 *     is offered as the evidence.
 *
 * So a source is a number in VALUE position: a JSON number the tool returned, or a string that is
 * entirely a number (a typed read often arrives as "4.21"). Numbers embedded in prose are excluded
 * — a message saying "delivered in 30ms" did not measure 30 of anything the answer is about.
 *
 * The tool's output is a message and its data concatenated, so every JSON object in it is walked
 * and the prose between them ignored.
 */
function reportedNumbers(output: string): Set<string> {
  const out = new Set<string>();
  const take = (v: unknown): void => {
    if (typeof v === 'number' && Number.isFinite(v)) out.add(canonical(v));
    else if (typeof v === 'string') {
      // WHOLLY a number, not prose that contains one.
      const t = v.trim();
      if (t && /^-?\d[\d,]*\.?\d*(?:[eE][+-]?\d+)?$/.test(t)) {
        const n = Number(t.replace(/,/g, ''));
        if (Number.isFinite(n)) out.add(canonical(n));
      }
    } else if (Array.isArray(v)) for (const x of v) take(x);
    else if (v && typeof v === 'object') for (const x of Object.values(v)) take(x);
  };
  for (const chunk of jsonChunks(output)) {
    try {
      take(JSON.parse(chunk));
    } catch {
      /* not JSON after all — it sources nothing */
    }
  }
  return out;
}

/** The JSON objects/arrays inside a tool's output, which is prose and data concatenated. Scans for
 *  balanced braces rather than assuming the shape, so a message containing a "{" cannot swallow the
 *  data that follows it. */
function jsonChunks(text: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < text.length; i++) {
    const open = text[i];
    if (open !== '{' && open !== '[') continue;
    const close = open === '{' ? '}' : ']';
    let depth = 0;
    let inStr = false;
    let esc = false;
    for (let j = i; j < text.length; j++) {
      const ch = text[j];
      if (inStr) {
        if (esc) esc = false;
        else if (ch === '\\') esc = true;
        else if (ch === '"') inStr = false;
        continue;
      }
      if (ch === '"') inStr = true;
      else if (ch === open) depth++;
      else if (ch === close) {
        depth--;
        if (depth === 0) {
          out.push(text.slice(i, j + 1));
          i = j;
          break;
        }
      }
    }
  }
  return out;
}

export function citationsFor(deliverableJson: string, calls: readonly RecordedCall[]): Citation[] {
  if (!calls.length) return [];
  let parsed: unknown;
  try {
    parsed = deliverableJson.trim() ? JSON.parse(deliverableJson) : undefined;
  } catch {
    return [];
  }

  const out: Citation[] = [];
  for (const v of assertedNumbers(parsed)) {
    // Citing "3" to whichever call happened to contain a 3 is a coincidence dressed as provenance.
    if (isIncidental(v)) continue;
    const sources = calls.filter((c) => reportedNumbers(c.output).has(v)).map(describe);
    if (!sources.length) continue; // no source is `unsourcedValues`'s finding, not a citation
    out.push({ value: v, sources: [...new Set(sources)] });
    if (out.length >= MAX_REPORTED) break;
  }
  return out;
}

/*
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * A SOURCE THAT WAS NEVER RETURNED.
 *
 * The same question, asked of the web rather than the plant, and the failure it catches is the most
 * common one there is: a model that cites a plausible URL it never saw. A reader treats a link as
 * evidence — it looks checkable, so it does not get checked — which makes an invented one worse
 * than no citation at all.
 *
 * Exactly as decidable as the numeric case, and for the same reason: we know what came back. A URL
 * in the answer either appears in a result the agent actually received, or it does not.
 *
 * Bare hosts are ignored. An agent naming "wikipedia.org" in a sentence is describing where
 * something is generally known to live, not claiming to have read a page; the fabrication that
 * matters is a specific link presented as the thing that was consulted.
 */
export function uncitedSources(deliverableJson: string, summary: string, calls: readonly RecordedCall[]): string[] {
  const received = new Set<string>();
  for (const c of calls) for (const u of urlsIn(c.output)) received.add(u);

  const out: string[] = [];
  const seen = new Set<string>();
  for (const u of urlsIn(`${deliverableJson}\n${summary}`)) {
    if (received.has(u) || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
    if (out.length >= MAX_REPORTED) break;
  }
  return out;
}

/** URLs, normalised so a trailing slash or a tracking parameter is not a different source. Only
 *  links with a PATH — a bare host is a mention, not a citation. */
function urlsIn(text: string): Set<string> {
  const out = new Set<string>();
  /*
   * THE CHARACTERS THAT CANNOT BE IN A URL ARE WHERE THE URL ENDS. RFC 3986 excludes space, " < >
   * \ ^ ` { and | from every production — they have to be percent-encoded — so a match that ran
   * through one of them swallowed the text AROUND the link, not part of it.
   *
   * The backtick is the one that bit, and it bit on the most ordinary thing a model writes: a link
   * inside markdown code fences. `new URL` dutifully re-encodes the trailing backtick as %60, the
   * key no longer matches the identical URL that came back from the search, and a correctly cited
   * page is reported as invented — observed live on a real answer that had cited it properly.
   *
   * A marker that fires on honest answers is ignored within a day, and takes the true findings with
   * it. So the boundary set is the RFC's, not a list of the delimiters seen so far.
   */
  for (const m of text.matchAll(/https?:\/\/[^\s"'<>)\]},`\\^{|]+/gi)) {
    try {
      // TRAILING PUNCTUATION IS THE SENTENCE, NOT THE LINK. A URL that ends a sentence picks up its
      // full stop, and comparing "a-real-page." against "a-real-page" reports a correctly cited page
      // as invented — a false accusation on the most ordinary way anyone writes a citation.
      const u = new URL(m[0].replace(/[.,;:!?]+$/, ''));
      if (u.pathname === '/' || u.pathname === '') continue; // a bare host is not a citation
      out.add(`${u.host}${u.pathname.replace(/\/$/, '')}`.toLowerCase());
    } catch {
      /* not a URL after all */
    }
  }
  return out;
}

/** What to tell the model about a source it never received. Flat: this one is not ambiguous. */
export function uncitedRemedy(urls: readonly string[]): string {
  return (
    `you cite ${urls.join(', ')}, and no search result you received contains ${urls.length === 1 ? 'that link' : 'those links'}. ` +
    'Cite only pages that came back from a search, or say plainly that you are answering without a source.'
  );
}
